import type { ComponentProps, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown } from 'lucide-react'
import { Button } from '@/components/shadcn/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/shadcn/dropdown-menu'
import { cn } from '@/lib/utils'

type ButtonProps = ComponentProps<typeof Button>

interface Props extends ButtonProps {
  /** Dropdown content — `DropdownMenuItem`s (plus separators/labels if needed). */
  menu: ReactNode
  /** Where the menu aligns to the arrow. Default `end`, so it doesn't overflow to the right. */
  menuAlign?: 'start' | 'center' | 'end'
  /** Disables only the arrow; `disabled` disables both halves. */
  menuDisabled?: boolean
  /** Accessible name of the arrow. Defaults to `common:buttons.moreActions`. */
  menuLabel?: string
  /** Classes for the wrapper; `className` styles the main button (as on `Button`). */
  containerClassName?: string
}

/**
 * Button with a primary action plus a dropdown of secondary ones: the left half behaves like a
 * normal `Button` (all its props, including `variant`/`size`, are forwarded), the arrow on the
 * right opens `menu`.
 */
export function SplitButton({
  menu,
  menuAlign = 'end',
  menuDisabled,
  menuLabel,
  containerClassName,
  className,
  variant,
  size,
  disabled,
  ...buttonProps
}: Props) {
  const { t } = useTranslation('common')

  return (
    <div className={cn('inline-flex items-stretch', containerClassName)}>
      <Button
        variant={variant}
        size={size}
        disabled={disabled}
        className={cn('rounded-r-none', className)}
        {...buttonProps}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant={variant}
            size={size}
            disabled={disabled || menuDisabled}
            // `border-current` instead of a fixed color so the divider works in every variant.
            className="rounded-l-none border-l border-current/20 px-1.5"
            aria-label={menuLabel ?? t('buttons.moreActions')}
          >
            <ChevronDown className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        {/* w-auto overrides the default "as wide as the trigger" — the trigger is just the
            arrow, which would wrap the item labels. */}
        <DropdownMenuContent align={menuAlign} className="w-auto">
          {menu}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
